import { Dispatch, FC, SetStateAction, useContext } from "react";
import { PatternOfCareProviderLookupSearchModel } from "../interfaces";
import { UserContext } from "@app/user/UserContext";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { checkForEnter } from "@app/shared/utility/checkForEnter";
import { FeatureFlagContextProps } from "@app/feature-flags/FeatureFlagContext";

interface pocpplsearchboxProps {
  handleClear: () => void;
  handleFieldUpdate: (key: keyof PatternOfCareProviderLookupSearchModel, value: string) => void;
  handleSearch: () => void;
  isFiltered: boolean;
  isLoading: boolean;
  searchState: PatternOfCareProviderLookupSearchModel;
  setSearchState: Dispatch<SetStateAction<PatternOfCareProviderLookupSearchModel>>;
}

export const PatternOfCareProviderLookupSearchBox: FC<pocpplsearchboxProps> = (props: pocpplsearchboxProps & FeatureFlagContextProps) => {
  const user = useContext(UserContext);
  const { npi, tin, city, state, zip } = props.searchState;

  const isSearchDisabled = props.isLoading || (
       !npi.trim() && !tin.trim()
  );
  const isClearDisbled = props.isLoading || !props.isFiltered;

  const requiredSearchLabel = "These fields search for an exact match. NPI" + (user.roles.includes(Roles.TIN_VIEWER) ? " or TIN" : "") + " is required.";

  const getSearchAndClearButtons = (
    <>
      <QESButton
        className="col-1"
        qesType="secondary"
        qesTestId="pattern-of-care-panel__search"
        disabled={isSearchDisabled}
        onClick={props.handleSearch}
      >
        {props.isLoading ? <QesIcon iconName={FontAwesome.Spinner} className="qes-icon-spin" description="Loading" /> : "Search"}
      </QESButton>

      <QESButton
        className="col-1"
        qesType="secondary-danger"
        qesTestId="pattern-of-care-panel__clear"
        disabled={isClearDisbled}
        onClick={props.handleClear}
      >
        Clear
      </QESButton>
    </>
  );

  return (
    <div className="pattern-of-care__input-fields">
      
      <p className="ms-3">{requiredSearchLabel}</p>
      
      <div className="mb-3">
        <div className="col-10 d-flex">

          <QESTextInput
            label="NPI"
            testId="pattern-of-care-panel__npi"
            containerClassName="col-3"
            onChange={(e) => { props.handleFieldUpdate("npi", e.target.value) }}
            value={npi}
            onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
          />

          {user.roles.includes(Roles.TIN_VIEWER) && (
            <QESTextInput
              label="TIN"
              testId="pattern-of-care-panel__tin"
              containerClassName="col-3"
              onChange={(e) => { props.handleFieldUpdate("tin", e.target.value) }}
              value={tin}
              onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
            />
          )}

        </div>
      </div>
      <div className="d-flex">
        <div className="col-10 d-flex">
            <>
              <QESTextInput
                label="City"
                testId="pattern-of-care-panel__city"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("city", e.target.value) }}
                value={city}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="State Abbreviation"
                testId="pattern-of-care-panel__state-abbreviation"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("state", e.target.value) }}
                pattern="/[a-zA-Z]{0,2}/"
                value={state}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="Zip"
                testId="pattern-of-care-panel__zip"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("zip", e.target.value) }}
                pattern="/\d{0,5}/"
                value={zip}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />
            </>
          
          {getSearchAndClearButtons}
        </div>
      </div>
    </div>
  );
}